import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import { useDeleteModal } from '../hooks/useDeleteModal';
import { useEffectiveCity } from '../hooks/useEffectiveCity';


function Escolas() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const userCity = useEffectiveCity();

  // Estados
  const [escolas, setEscolas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [filtroZona, setFiltroZona] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingEscola, setEditingEscola] = useState(null);
  const [formData, setFormData] = useState({
    school: '',
    director_name: '',
    director_phone: '',
    address: '',
    codigo_ideb: '',
    is_secretaria: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Modal de exclusão
  const {
    isDeleteModalOpen,
    itemToDelete,
    openDeleteModal,
    closeDeleteModal
  } = useDeleteModal();

  // Modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEscola, setSelectedEscola] = useState(null);
  const [detailTurmas, setDetailTurmas] = useState([]);
  const [loadingTurmas, setLoadingTurmas] = useState(false);

  // Debounce do campo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Resetar página ao mudar filtro de zona
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroZona]);

  // Buscar dados com paginação server-side
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { page: currentPage };
      if (userCity?.id) params.city = userCity.id;
      if (debouncedSearch) params.search = debouncedSearch;
      if (filtroZona) params.zona = filtroZona;

      const response = await apiGet('/schools/paginated/', params);
      setEscolas(response.results || []);
      setTotalCount(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / 100));
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filtroZona, userCity?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);



  // Determinar zona baseado no endereço (simplificado)
  const getZona = (escola) => {
    const address = escola.address?.toLowerCase() || '';
    if (address.includes('rural') || address.includes('zona rural') || address.includes('fazenda') || address.includes('sitio')) {
      return 'Rural';
    }
    return 'Urbana';
  };

  // Verificar se pode criar/editar
  // Permitido: Admin, Coordenador e Supervisor.
  // Bloqueado: Gestor, Referência Técnica e Professor (apenas leitura), e o
  // Diretor(a), que só muda a situação dos alunos (api/authentication.py).
  const canEdit = user?.role === ROLES.ADMIN
    || user?.role === ROLES.COORDENADOR
    || user?.role === ROLES.SUPERVISOR;

  // Abrir modal para criar nova escola
  const handleCreate = () => {
    setEditingEscola(null);
    setFormData({
      school: '',
      director_name: '',
      director_phone: '',
      address: '',
      codigo_ideb: '',
      is_secretaria: false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Abrir modal para editar escola
  const handleEdit = (escola) => {
    setEditingEscola(escola);
    setFormData({
      school: escola.school || '',
      director_name: escola.director_name || '',
      director_phone: escola.director_phone || '',
      address: escola.address || '',
      codigo_ideb: escola.codigo_ideb || '',
      is_secretaria: !!escola.is_secretaria,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Abrir modal de detalhes (carrega turmas sob demanda)
  const handleViewDetails = async (escola) => {
    setSelectedEscola(escola);
    setShowDetailsModal(true);
    setDetailTurmas([]);
    setLoadingTurmas(true);
    try {
      const turmas = await apiGet(`/schools/${escola.id}/turmas/`);
      setDetailTurmas(turmas);
    } catch (err) {
      console.error('Erro ao buscar turmas:', err);
    } finally {
      setLoadingTurmas(false);
    }
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};
    if (!formData.school.trim()) errors.school = 'Nome da escola é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar escola (criar ou atualizar)
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
      };

      if (editingEscola) {
        await apiPut(`/schools/${editingEscola.id}/`, dataToSend);
      } else {
        await apiPost('/schools/', dataToSend);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar escola:', err);
      setFormErrors({ submit: err.message || 'Erro ao salvar escola' });
    } finally {
      setSaving(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (escola) => {
    openDeleteModal(escola);
  };

  // Executar exclusão
  const handleDelete = async (escola) => {
    await apiDelete(`/schools/${escola.id}/`);
    await fetchData();
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltroZona('');
    setSearchTerm('');
  };

  // Calcular estatísticas totais da página atual
  const totalAlunos = escolas.reduce((acc, escola) => acc + (escola.total_alunos || 0), 0);
  const totalTurmas = escolas.reduce((acc, escola) => acc + (escola.total_turmas || 0), 0);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Escolas..."
        subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error}</p>
          <button
            onClick={fetchData}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Escolas
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Gerencie as escolas do sistema
            {userCity && <span className="text-sideb-blue"> - {userCity.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          {canEdit && (
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Nova Escola
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Busca */}
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Buscar escola, diretor, endereço ou cidade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />
          </div>

          {/* Filtro por Zona */}
          <SearchableSelect
            value={filtroZona}
            onChange={setFiltroZona}
            options={['Urbana', 'Rural']}
            emptyOptionLabel="Todas as zonas"
            darkMode={darkMode}
            className="min-w-[160px]"
          />
        </div>

        {/* Limpar filtros */}
        {(filtroZona || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className={`text-sm ${darkMode ? 'text-[#0974BA] hover:text-[#0974BA]' : 'text-[#0974BA] hover:text-[#0974BA]'}`}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {totalCount}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Escolas</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {escolas.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nesta Página</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {totalTurmas}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Turmas</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {totalAlunos}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Alunos</div>
        </div>
      </div>

      {/* Lista de Escolas */}
      {escolas.length === 0 ? (
        <div className={`rounded-xl p-12 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-5xl mb-4">🏫</div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Nenhuma escola encontrada
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {(debouncedSearch || filtroZona)
              ? 'Nenhuma escola corresponde aos filtros aplicados.'
              : 'Não há escolas cadastradas ainda.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {escolas.map((escola) => {
            const zona = getZona(escola);

            return (
              <div
                key={escola.id}
                className={`rounded-xl p-6 border transition-colors ${darkMode
                  ? 'bg-slate-800/70 border-slate-700/10 hover:border-sideb-blue/50'
                  : 'bg-white/90 border-slate-200 shadow-sm hover:border-[#0974BA]'
                  }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-semibold text-lg ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      {escola.school}
                    </h3>
                    {escola.is_secretaria && (
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold ${darkMode
                        ? 'bg-purple-500/20 text-purple-300'
                        : 'bg-purple-100 text-purple-700'
                      }`}>
                        Secretaria
                      </span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${zona === 'Urbana'
                    ? 'bg-sideb-blue/20 text-sideb-blue'
                    : 'bg-sideb-yellow/20 text-sideb-yellow'
                    }`}>
                    {zona}
                  </span>
                </div>

                {escola.director_name && (
                  <p className={`text-sm mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="font-medium">Diretor:</span> {escola.director_name}
                  </p>
                )}

                {escola.director_phone && (
                  <p className={`text-sm mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="font-medium">Telefone:</span> {escola.director_phone}
                  </p>
                )}

                {escola.city_name && (
                  <p className={`text-sm mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="font-medium">Cidade:</span> {escola.city_name} - {escola.state}
                  </p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Alunos</span>
                    <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{escola.total_alunos}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Turmas</span>
                    <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{escola.total_turmas}</span>
                  </div>
                  {escola.codigo_ideb && (
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Código IDEB</span>
                      <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{escola.codigo_ideb}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleViewDetails(escola)}
                    className={`flex-1 py-2 rounded-lg text-sm transition-colors ${darkMode
                      ? 'bg-sideb-blue/20 hover:bg-sideb-blue/30 text-sideb-blue'
                      : 'bg-[#0974BA]/10 hover:bg-[#0974BA]/20 text-[#0974BA]'
                      }`}
                  >
                    Ver Detalhes
                  </button>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => handleEdit(escola)}
                        className={`p-2 rounded-lg transition-colors ${darkMode
                          ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                          : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                          }`}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClick(escola)}
                        className={`p-2 rounded-lg transition-colors ${darkMode
                          ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                          : 'hover:bg-[#E83A3C]/5 text-slate-500 hover:text-[#E83A3C]'
                          }`}
                        title="Excluir"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === 1
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:-translate-y-0.5'
              } ${darkMode
                ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
          >
            Anterior
          </button>
          <span className={`text-sm px-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === totalPages
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:-translate-y-0.5'
              } ${darkMode
                ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {editingEscola ? 'Editar Escola' : 'Nova Escola'}
            </h2>

            <div className="space-y-4">
              {/* Nome da Escola */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome da Escola *
                </label>
                <input
                  type="text"
                  value={formData.school}
                  onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.school ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: EM João Paulo II"
                />
                {formErrors.school && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.school}</p>
                )}
              </div>

              {/* Diretor */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome do Diretor
                </label>
                <input
                  type="text"
                  value={formData.director_name}
                  onChange={(e) => setFormData({ ...formData, director_name: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: Maria da Silva"
                />
              </div>

              {/* Telefone do Diretor */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Telefone do Diretor
                </label>
                <input
                  type="tel"
                  value={formData.director_phone}
                  onChange={(e) => setFormData({ ...formData, director_phone: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: (99) 99999-9999"
                />
              </div>

              {/* Endereço */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Endereço
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: Rua das Flores, 123 - Centro"
                />
              </div>

              {/* Código IDEB */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Código IDEB
                </label>
                <input
                  type="text"
                  value={formData.codigo_ideb}
                  onChange={(e) => setFormData({ ...formData, codigo_ideb: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: 12345678"
                />
              </div>

              {/* Secretaria Municipal */}
              <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${darkMode
                ? 'bg-slate-700/30 hover:bg-slate-700/50'
                : 'bg-slate-50 hover:bg-slate-100'
              }`}>
                <input
                  type="checkbox"
                  checked={!!formData.is_secretaria}
                  onChange={(e) => setFormData({ ...formData, is_secretaria: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                />
                <span className="text-sm">
                  <span className={`font-medium block ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Esta unidade é a Secretaria Municipal de Educação
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Marque para permitir vincular Referência Técnica do Município a esta unidade.
                  </span>
                </span>
              </label>

              {formErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{formErrors.submit}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                  }`}
              >
                {saving ? 'Salvando...' : editingEscola ? 'Salvar Alterações' : 'Criar Escola'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        item={itemToDelete}
        itemName={itemToDelete?.school}
        itemType="Escola"
        warningMessage="Todos os dados vinculados a esta escola serão afetados. Digite sua senha para confirmar."
      />

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedEscola && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <div className="flex justify-between items-start mb-6">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {selectedEscola.school}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-700 text-slate-400'
                  : 'hover:bg-slate-100 text-slate-500'
                  }`}
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Informações Gerais */}
              <div>
                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Informações Gerais
                </h3>
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Diretor</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedEscola.director_name || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Telefone do Diretor</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedEscola.director_phone || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cidade</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedEscola.city_name ? `${selectedEscola.city_name} - ${selectedEscola.state}` : '-'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Endereço</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedEscola.address || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Código IDEB</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedEscola.codigo_ideb || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Zona</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {getZona(selectedEscola)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estatísticas */}
              <div>
                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Estatísticas
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`rounded-xl p-4 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                      {selectedEscola.total_turmas || 0}
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turmas</p>
                  </div>
                  <div className={`rounded-xl p-4 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                      {selectedEscola.total_alunos || 0}
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alunos</p>
                  </div>
                </div>
              </div>

              {/* Lista de Turmas */}
              {loadingTurmas ? (
                <div className={`text-center py-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <p className="text-sm">Carregando turmas...</p>
                </div>
              ) : detailTurmas.length > 0 && (
                <div>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Turmas
                  </h3>
                  <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                          <th className={`text-left px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Turma</th>
                          <th className={`text-left px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Série</th>
                          <th className={`text-left px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Turno</th>
                          <th className={`text-right px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Alunos</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-slate-600' : 'divide-slate-200'}`}>
                        {detailTurmas.map(turma => (
                          <tr key={turma.id}>
                            <td className={`px-4 py-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{turma.class_name}</td>
                            <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{turma.grade || '-'}</td>
                            <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{turma.shift || '-'}</td>
                            <td className={`px-4 py-2 text-right ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              {turma.total_alunos}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Escolas;
